// nav/last-room.js: B3b F7 "rejoin your game" memory.
//
// Remembers the last room code the user joined (with a timestamp) so the
// home page can offer a way back after a closed tab. Entries older than
// 2 hours are ignored. All storage access is wrapped in try/catch with
// a console warning; when storage is unavailable the helpers return
// null / do nothing and the page works without the button.

export const LAST_ROOM_KEY = 'euchredev_last_room_v1';
export const LAST_ROOM_MAX_AGE_MS = 2 * 60 * 60 * 1000;
// While the player stays in the room, the stored time is renewed this
// often, so a long game never ages out of the 2-hour limit.
export const LAST_ROOM_RENEW_MS = 60_000;
const LAST_ROOM_CHANGED_EVENT = 'euchre:last-room-changed';

/**
 * @typedef {{ getItem?: (k: string) => string|null, setItem?: (k: string, v: string) => void, removeItem?: (k: string) => void }} StorageLike
 */

/**
 * @param {StorageLike|null|undefined} [storage]
 * @returns {StorageLike|null}
 */
function pickStorage(storage) {
  if (storage) return storage;
  try {
    if (typeof localStorage !== 'undefined') return localStorage;
  } catch (err) {
    console.warn('[last-room] localStorage is not reachable:', err instanceof Error ? err.message : err);
    return null;
  }
  return null;
}

/**
 * Remember a room code as the last-joined room.
 *
 * @param {string} code
 * @param {StorageLike|null|undefined} [storage]
 */
export function saveLastRoom(code, storage) {
  if (typeof code !== 'string' || code.length === 0) return;
  const store = pickStorage(storage);
  if (!store || typeof store.setItem !== 'function') return;
  try {
    store.setItem(LAST_ROOM_KEY, JSON.stringify({ code, ts: Date.now() }));
  } catch (err) {
    console.warn('[last-room] could not remember the room:', err instanceof Error ? err.message : err);
  }
}

export function clearLastRoom(reason, storage) {
  const store = pickStorage(storage);
  if (!store) return;
  try {
    if (typeof store.removeItem === 'function') store.removeItem(LAST_ROOM_KEY);
    else if (typeof store.setItem === 'function') store.setItem(LAST_ROOM_KEY, '');
  } catch (err) {
    console.warn(`[last-room] could not clear the room after ${reason}:`, err instanceof Error ? err.message : err);
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new window.Event(LAST_ROOM_CHANGED_EVENT));
}

/**
 * Why the stored room should be forgotten, or null to keep it. A
 * ROOM_NOT_FOUND only counts when the failed join was for the stored
 * code: a mistyped code must not wipe the Rejoin button.
 *
 * @param {{ removed: unknown, status: string, error: { code?: string }|null, joiningCode?: string|null }} state
 * @param {string|null} [storedCode]
 * @returns {string|null}
 */
export function lastRoomExitReason(state, storedCode = null) {
  if (state.removed) return 'removed';
  if (state.status !== 'error') return null;
  if (state.error?.code === 'NOT_IN_ROOM') return 'NOT_IN_ROOM';
  if (state.error?.code === 'ROOM_NOT_FOUND' && storedCode && state.joiningCode === storedCode) {
    return 'ROOM_NOT_FOUND';
  }
  return null;
}

/**
 * The useRoomWs effect body. In a room: save the code now and renew
 * its time every minute; the returned function stops the renewal.
 * Out of a room: forget the stored code when the exit means the room
 * is gone for this player.
 *
 * @param {{ removed: unknown, status: string, error: { code?: string }|null, roomCode: string|null, joiningCode?: string|null }} state
 * @param {StorageLike|null|undefined} [storage]
 * @returns {(() => void)|undefined}
 */
export function lastRoomEffect(state, storage) {
  if (state.status === 'in_room' && state.roomCode) {
    const code = state.roomCode;
    saveLastRoom(code, storage);
    const timer = setInterval(() => saveLastRoom(code, storage), LAST_ROOM_RENEW_MS);
    return () => clearInterval(timer);
  }
  const reason = lastRoomExitReason(state, loadLastRoom(Date.now(), storage));
  if (reason) clearLastRoom(reason, storage);
  return undefined;
}

/**
 * Call onChange when the stored room changes in this tab (our own
 * event) or in another tab (the window "storage" event; a null key
 * means the whole store was cleared). Returns the unsubscribe function.
 *
 * @param {EventTarget} target  usually window
 * @param {() => void} onChange
 * @returns {() => void}
 */
export function subscribeLastRoom(target, onChange) {
  const onStorage = (/** @type {any} */ event) => {
    if (event.key == null || event.key === LAST_ROOM_KEY) onChange();
  };
  target.addEventListener(LAST_ROOM_CHANGED_EVENT, onChange);
  target.addEventListener('storage', onStorage);
  return () => {
    target.removeEventListener(LAST_ROOM_CHANGED_EVENT, onChange);
    target.removeEventListener('storage', onStorage);
  };
}

/**
 * The last-joined room code, or null when there is none, it expired
 * (older than 2 hours), or storage cannot be read.
 *
 * @param {number} [nowMs]
 * @param {StorageLike|null|undefined} [storage]
 * @returns {string|null}
 */
export function loadLastRoom(nowMs = Date.now(), storage) {
  const store = pickStorage(storage);
  if (!store || typeof store.getItem !== 'function') return null;
  let raw = null;
  try {
    raw = store.getItem(LAST_ROOM_KEY);
  } catch (err) {
    console.warn('[last-room] could not read the remembered room:', err instanceof Error ? err.message : err);
    return null;
  }
  if (!raw) return null;
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    console.warn('[last-room] the remembered room is unreadable:', err instanceof Error ? err.message : err);
    return null;
  }
  if (!parsed || typeof parsed.code !== 'string' || parsed.code.length === 0) return null;
  if (typeof parsed.ts !== 'number' || !Number.isFinite(parsed.ts)) return null;
  if (nowMs - parsed.ts > LAST_ROOM_MAX_AGE_MS) return null;
  return parsed.code;
}
